import type { FormEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import { ErrorNotice, LoadingView } from "../../components/feedback";
import { INPUT_LIMITS, PAGE_LIMITS } from "../../constants";
import { createApiPath, requestAdminApi } from "../../lib/api-client";
import type {
  AdminUser,
  AdminUserList,
  AnnouncementLocale,
  PushAudience,
  PushCampaign,
  PushPublication,
  PushTargetType,
} from "../../types";
import { AnnouncementLocaleTabs } from "./announcement-locale-tabs";

type PushComposeScreenProps = {
  campaignId: string | null;
  onBack: () => void;
  onPublished: () => void;
};

/**
 * 配信対象種別として扱える値か判定する
 *
 * @param value - 判定対象
 * @returns 配信対象種別の場合はtrue
 */
const isPushTargetType = (value: string): value is PushTargetType => {
  return value === "all" || value === "selected";
};

/**
 * Push配信作成画面
 * @param props.campaignId - 編集対象の配信ID。新規作成時はnull
 * @param props.onBack - 配信履歴へ戻る処理
 * @param props.onPublished - 配信完了後の処理
 * @returns Push配信作成画面
 */
export const PushComposeScreen = ({ campaignId, onBack, onPublished }: PushComposeScreenProps) => {
  // 日本語タイトルを保持する
  const [japaneseTitle, setJapaneseTitle] = useState("");

  // 日本語本文を保持する
  const [japaneseBody, setJapaneseBody] = useState("");

  // 英語タイトルを保持する
  const [englishTitle, setEnglishTitle] = useState("");

  // 英語本文を保持する
  const [englishBody, setEnglishBody] = useState("");

  // 表示中のお知らせ本文の言語を保持する
  const [activeLocale, setActiveLocale] = useState<AnnouncementLocale>("en");

  // 配信対象種別を保持する
  const [targetType, setTargetType] = useState<PushTargetType>("all");

  // 選択されたユーザーIDを保持する
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  // ユーザー検索語を保持する
  const [userQuery, setUserQuery] = useState("");

  // ユーザー検索結果を保持する
  const [userResults, setUserResults] = useState<AdminUser[]>([]);

  // 編集中の配信IDを保持する
  const [currentCampaignId, setCurrentCampaignId] = useState(campaignId);

  // 配信対象件数を保持する
  const [audience, setAudience] = useState<PushAudience | null>(null);

  // 配信確認への同意を保持する
  const [isPublishConfirmed, setIsPublishConfirmed] = useState(false);

  // 同じ下書きの配信再試行に使う冪等キーを保持する
  const [publishIdempotencyKey, setPublishIdempotencyKey] = useState<string | null>(null);

  // 編集対象の読み込み中かを保持する
  const [isLoadingCampaign, setIsLoadingCampaign] = useState(campaignId !== null);

  // ユーザー検索中かを保持する
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);

  // 下書き保存中かを保持する
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  // 配信処理中かを保持する
  const [isPublishing, setIsPublishing] = useState(false);

  // 下書き入力のエラーを保持する
  const [draftError, setDraftError] = useState<string | null>(null);

  // 編集対象取得のエラーを保持する
  const [campaignError, setCampaignError] = useState<string | null>(null);

  // 完了通知を保持する
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // 日本語と英語が公開可能な状態か判定する
  const hasCompleteLocalizations = useMemo(() => {
    return (
      japaneseTitle.trim().length > 0 &&
      japaneseBody.trim().length > 0 &&
      englishTitle.trim().length > 0 &&
      englishBody.trim().length > 0
    );
  }, [englishBody, englishTitle, japaneseBody, japaneseTitle]);

  const isJapaneseLocale = activeLocale === "ja";
  const activeTitle = isJapaneseLocale ? japaneseTitle : englishTitle;
  const activeBody = isJapaneseLocale ? japaneseBody : englishBody;
  const activeLocaleComplete = activeTitle.trim().length > 0 && activeBody.trim().length > 0;

  // 編集対象が指定されている場合に配信内容を読み込む
  useEffect(() => {
    if (campaignId === null) {
      setIsLoadingCampaign(false);
      setCampaignError(null);
      return;
    }

    const loadCampaign = async () => {
      setIsLoadingCampaign(true);
      setCampaignError(null);

      try {
        const campaign = await requestAdminApi<PushCampaign>(
          `/v1/admin/push-campaigns/${encodeURIComponent(campaignId)}`,
        );
        setCurrentCampaignId(campaign.campaign_id);
        setJapaneseTitle(campaign.localizations.ja.title);
        setJapaneseBody(campaign.localizations.ja.body);
        setEnglishTitle(campaign.localizations.en.title);
        setEnglishBody(campaign.localizations.en.body);
        setTargetType(campaign.target_type);
        setSelectedUserIds(campaign.target_user_ids);
      } catch (error: unknown) {
        console.error("Failed to load push campaign", error);
        setCampaignError("配信内容を読み込めませんでした。");
      } finally {
        setIsLoadingCampaign(false);
      }
    };

    void loadCampaign();
  }, [campaignId]);

  const resetPreview = () => {
    setAudience(null);
    setIsPublishConfirmed(false);
    setPublishIdempotencyKey(null);
    setSuccessMessage(null);
  };

  const validateDraft = () => {
    if (targetType === "selected" && selectedUserIds.length === 0) {
      return "配信するユーザーを1人以上選択してください。";
    }

    return null;
  };

  const saveDraft = async () => {
    const validationMessage = validateDraft();

    if (validationMessage) {
      setDraftError(validationMessage);
      return null;
    }

    const payload = {
      localizations: {
        en: { body: englishBody.trim(), title: englishTitle.trim() },
        ja: { body: japaneseBody.trim(), title: japaneseTitle.trim() },
      },
      target_type: targetType,
      target_user_ids: targetType === "selected" ? selectedUserIds : [],
    };
    const path = currentCampaignId
      ? `/v1/admin/push-campaigns/${encodeURIComponent(currentCampaignId)}`
      : "/v1/admin/push-campaigns";
    const method = currentCampaignId ? "PATCH" : "POST";
    const savedCampaign = await requestAdminApi<PushCampaign>(path, { method, body: payload });

    setCurrentCampaignId(savedCampaign.campaign_id);
    return savedCampaign;
  };

  const handleSearchUsers = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedQuery = userQuery.trim();

    if (!normalizedQuery) {
      setDraftError("名前またはメールアドレスを入力してください。");
      return;
    }

    setIsSearchingUsers(true);
    setDraftError(null);

    try {
      const path = createApiPath("/v1/admin/users", {
        query: normalizedQuery,
        limit: PAGE_LIMITS.users,
      });
      const result = await requestAdminApi<AdminUserList>(path);
      setUserResults(result.users);
    } catch (error: unknown) {
      console.error("Failed to search admin users", error);
      setDraftError("ユーザーを検索できませんでした。");
    } finally {
      setIsSearchingUsers(false);
    }
  };

  const handleToggleUser = (userId: string) => {
    setSelectedUserIds((currentUserIds) => {
      if (currentUserIds.includes(userId)) {
        return currentUserIds.filter((currentUserId) => currentUserId !== userId);
      }

      return [...currentUserIds, userId];
    });
    resetPreview();
  };

  const handleSaveAndPreview = async () => {
    setIsSavingDraft(true);
    setDraftError(null);
    setSuccessMessage(null);

    try {
      const savedCampaign = await saveDraft();

      if (!savedCampaign) {
        return;
      }

      const result = await requestAdminApi<PushAudience>(
        `/v1/admin/push-campaigns/${encodeURIComponent(savedCampaign.campaign_id)}/audience`,
      );
      setAudience(result);
      setPublishIdempotencyKey((currentKey) => {
        return currentKey ?? crypto.randomUUID();
      });
      setSuccessMessage("下書きを保存し、配信対象を確認しました。");
    } catch (error: unknown) {
      console.error("Failed to save push campaign draft", error);
      setDraftError("下書きを保存できませんでした。入力内容を確認してもう一度お試しください。");
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handlePublish = async () => {
    if (!hasCompleteLocalizations) {
      setDraftError("公開するには日本語と英語のタイトル・本文をすべて入力してください。");
      return;
    }

    if (!currentCampaignId || !audience || !isPublishConfirmed || !publishIdempotencyKey) {
      setDraftError("配信対象を確認し、最終確認にチェックを入れてください。");
      return;
    }

    setIsPublishing(true);
    setDraftError(null);
    setSuccessMessage(null);

    try {
      await requestAdminApi<PushPublication>(
        `/v1/admin/push-campaigns/${encodeURIComponent(currentCampaignId)}/publish`,
        {
          method: "POST",
          headers: { "Idempotency-Key": publishIdempotencyKey },
        },
      );
      setJapaneseTitle("");
      setJapaneseBody("");
      setEnglishTitle("");
      setEnglishBody("");
      setTargetType("all");
      setSelectedUserIds([]);
      setUserResults([]);
      setUserQuery("");
      setCurrentCampaignId(null);
      setAudience(null);
      setIsPublishConfirmed(false);
      setPublishIdempotencyKey(null);
      onPublished();
    } catch (error: unknown) {
      console.error("Failed to publish push campaign", error);
      setDraftError("配信を開始できませんでした。時間をおいてもう一度お試しください。");
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <p className="eyebrow">お知らせ管理</p>
          <h1>お知らせ配信</h1>
          <p>配信内容と対象件数を確認してから、Push通知とアプリ内のお知らせを公開します。</p>
        </div>
        <button className="button button--secondary" type="button" onClick={onBack}>
          配信履歴に戻る
        </button>
      </section>

      {successMessage ? (
        <div className="notice notice--success" role="status">
          <span aria-hidden="true">✓</span>
          <p>{successMessage}</p>
        </div>
      ) : null}

      {isLoadingCampaign ? (
        <LoadingView label="配信内容を読み込んでいます" />
      ) : campaignError ? (
        <ErrorNotice message={campaignError} />
      ) : (
        <section className="panel compose-panel" aria-labelledby="compose-title">
          <div className="panel__header">
            <div>
              <p className="eyebrow">配信内容</p>
              <h2 id="compose-title">{currentCampaignId ? "下書きを編集" : "新しい配信を作成"}</h2>
            </div>
            {currentCampaignId ? <span className="badge badge--draft">下書き保存済み</span> : null}
          </div>

          {draftError ? <ErrorNotice message={draftError} /> : null}

          <div className="form-stack localized-content-tabs">
            <AnnouncementLocaleTabs
              activeLocale={activeLocale}
              englishComplete={englishTitle.trim().length > 0 && englishBody.trim().length > 0}
              japaneseComplete={japaneseTitle.trim().length > 0 && japaneseBody.trim().length > 0}
              onChange={setActiveLocale}
            />
            <section
              className="localized-content"
              role="tabpanel"
              aria-label={
                isJapaneseLocale ? "日本語のお知らせ内容" : "English announcement content"
              }
            >
              <div className="localized-content__header">
                <h3>{isJapaneseLocale ? "日本語" : "English"}</h3>
                <span
                  className={`badge ${activeLocaleComplete ? "badge--completed" : "badge--draft"}`}
                >
                  {activeLocaleComplete ? "入力済み" : "入力途中"}
                </span>
              </div>
              {isJapaneseLocale ? (
                <>
                  <label>
                    <span>タイトル</span>
                    <input
                      type="text"
                      value={japaneseTitle}
                      maxLength={INPUT_LIMITS.pushTitle}
                      placeholder="例：新しい機能を追加しました"
                      onChange={(event) => {
                        setJapaneseTitle(event.target.value);
                        resetPreview();
                      }}
                    />
                    <small>
                      {japaneseTitle.length} / {INPUT_LIMITS.pushTitle}文字
                    </small>
                  </label>
                  <label>
                    <span>本文</span>
                    <textarea
                      value={japaneseBody}
                      maxLength={INPUT_LIMITS.pushBody}
                      rows={6}
                      placeholder="ユーザーへ届ける内容を入力してください。"
                      onChange={(event) => {
                        setJapaneseBody(event.target.value);
                        resetPreview();
                      }}
                    />
                    <small>
                      {japaneseBody.length} / {INPUT_LIMITS.pushBody}文字
                    </small>
                  </label>
                </>
              ) : (
                <>
                  <label>
                    <span>Title</span>
                    <input
                      type="text"
                      value={englishTitle}
                      maxLength={INPUT_LIMITS.pushTitle}
                      placeholder="Example: We added a new feature"
                      onChange={(event) => {
                        setEnglishTitle(event.target.value);
                        resetPreview();
                      }}
                    />
                    <small>
                      {englishTitle.length} / {INPUT_LIMITS.pushTitle}文字
                    </small>
                  </label>
                  <label>
                    <span>Body</span>
                    <textarea
                      value={englishBody}
                      maxLength={INPUT_LIMITS.pushBody}
                      rows={6}
                      placeholder="Enter the message shown to English users."
                      onChange={(event) => {
                        setEnglishBody(event.target.value);
                        resetPreview();
                      }}
                    />
                    <small>
                      {englishBody.length} / {INPUT_LIMITS.pushBody}文字
                    </small>
                  </label>
                </>
              )}
            </section>
          </div>

          <fieldset className="target-fieldset">
            <legend>配信対象</legend>
            <label className={`choice-card ${targetType === "all" ? "choice-card--selected" : ""}`}>
              <input
                type="radio"
                name="targetType"
                value="all"
                checked={targetType === "all"}
                onChange={(event) => {
                  if (isPushTargetType(event.target.value)) {
                    setTargetType(event.target.value);
                    resetPreview();
                  }
                }}
              />
              <span>
                <strong>すべてのユーザー</strong>
                <small>通知を受け取れるすべての端末へ配信します。</small>
              </span>
            </label>
            <label
              className={`choice-card ${targetType === "selected" ? "choice-card--selected" : ""}`}
            >
              <input
                type="radio"
                name="targetType"
                value="selected"
                checked={targetType === "selected"}
                onChange={(event) => {
                  if (isPushTargetType(event.target.value)) {
                    setTargetType(event.target.value);
                    resetPreview();
                  }
                }}
              />
              <span>
                <strong>ユーザーを選択</strong>
                <small>検索して選んだユーザーだけへ配信します。</small>
              </span>
            </label>
          </fieldset>

          {targetType === "selected" ? (
            <div className="user-picker">
              <form className="user-search" onSubmit={handleSearchUsers}>
                <label htmlFor="user-query">ユーザー検索</label>
                <div>
                  <input
                    id="user-query"
                    type="search"
                    value={userQuery}
                    placeholder="名前またはメールアドレス"
                    onChange={(event) => setUserQuery(event.target.value)}
                  />
                  <button
                    className="button button--secondary"
                    type="submit"
                    disabled={isSearchingUsers}
                  >
                    {isSearchingUsers ? "検索中…" : "検索"}
                  </button>
                </div>
              </form>

              {selectedUserIds.length > 0 ? (
                <fieldset className="selected-users">
                  <legend>選択中 {selectedUserIds.length}人</legend>
                  <div>
                    {selectedUserIds.map((userId, userIndex) => {
                      const user = userResults.find((result) => result.user_id === userId);
                      return (
                        <button key={userId} type="button" onClick={() => handleToggleUser(userId)}>
                          {user?.display_name || user?.email || `選択済みユーザー ${userIndex + 1}`}{" "}
                          <span aria-hidden="true">×</span>
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
              ) : null}

              {userResults.length > 0 ? (
                <div className="user-results">
                  {userResults.map((user) => {
                    const isSelected = selectedUserIds.includes(user.user_id);
                    const hasPushInstallation = user.push_installation_count > 0;
                    return (
                      <label key={user.user_id} aria-disabled={!hasPushInstallation}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={!hasPushInstallation}
                          onChange={() => handleToggleUser(user.user_id)}
                        />
                        <span className="user-result__avatar" aria-hidden="true">
                          {user.display_name.slice(0, 1).toUpperCase() || "?"}
                        </span>
                        <span>
                          <strong>{user.display_name}</strong>
                          <small>
                            {user.email || "メール未登録"} · 配信可能{" "}
                            {user.push_installation_count.toLocaleString("ja-JP")}端末
                          </small>
                        </span>
                      </label>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="preview-card">
            <div>
              <p className="eyebrow">{isJapaneseLocale ? "日本語プレビュー" : "English preview"}</p>
              <strong>
                {activeTitle.trim() || (isJapaneseLocale ? "通知タイトル" : "Notification title")}
              </strong>
              <p>
                {activeBody.trim() ||
                  (isJapaneseLocale
                    ? "ここに通知本文が表示されます。"
                    : "The notification body appears here.")}
              </p>
            </div>
          </div>

          <button
            className="button button--primary button--full"
            type="button"
            disabled={isSavingDraft}
            onClick={handleSaveAndPreview}
          >
            {isSavingDraft ? "確認しています…" : "下書きを保存して対象を確認"}
          </button>

          {audience ? (
            <section className="publish-confirmation" aria-labelledby="publish-confirmation-title">
              <div className="audience-counts">
                <div>
                  <strong>{audience.user_count.toLocaleString("ja-JP")}</strong>
                  <span>ユーザー</span>
                </div>
                <div>
                  <strong>{audience.installation_count.toLocaleString("ja-JP")}</strong>
                  <span>配信対象端末</span>
                </div>
              </div>
              <h3 id="publish-confirmation-title">配信前の最終確認</h3>
              <p>
                {audience.installation_count === 0
                  ? "Push通知対象端末はありませんが、アプリ内のお知らせとして公開されます。"
                  : "公開すると取り消せません。タイトル・本文・対象件数をもう一度確認してください。"}
              </p>
              <label className="confirmation-check">
                <input
                  type="checkbox"
                  checked={isPublishConfirmed}
                  onChange={(event) => setIsPublishConfirmed(event.target.checked)}
                />
                <span>内容と対象を確認し、この配信を開始します。</span>
              </label>
              <button
                className="button button--danger button--full"
                type="button"
                disabled={
                  !hasCompleteLocalizations ||
                  !isPublishConfirmed ||
                  isPublishing ||
                  audience.user_count === 0
                }
                onClick={handlePublish}
              >
                {isPublishing ? "配信を開始しています…" : "お知らせ配信を開始"}
              </button>
            </section>
          ) : null}
        </section>
      )}
    </div>
  );
};
